import { parse, stringify } from 'yaml';
import type { Approval, Checkpoint, Routing, RoutingContext } from './checkpoints';
import { type ClassificationAnswers, hasPersonalData } from './classification';
import { defaultRouting, fitToTeam } from './checkpoints';
import { type Profile, isProfile } from './profiles';
import { type Role, type RoleException, type RolesFile, isRole } from './roles';

export const ROLES_PATH = '.vivicta/asdp/roles.yaml';
export const PROFILE_REQUEST_PATH = '.vivicta/asdp/profile-request.yaml';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const logins = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    : [];

/** Reads roles.yaml; unknown roles and malformed entries are dropped. */
export function parseRolesFile(text: string): RolesFile {
  const doc: unknown = parse(text);
  if (!isObject(doc) || doc.schema_version !== 1 || !isObject(doc.roles)) {
    throw new Error('roles.yaml is not schema_version 1 with a roles map.');
  }
  const roles: RolesFile['roles'] = {};
  for (const [role, holders] of Object.entries(doc.roles)) {
    if (isRole(role)) roles[role] = logins(holders);
  }
  const exceptions: RoleException[] = (Array.isArray(doc.exceptions) ? doc.exceptions : [])
    .filter(isObject)
    .filter(e => typeof e.person === 'string' && isRole(e.role) && typeof e.reference === 'string')
    .map(e => ({
      person: e.person as string,
      role: e.role as Role,
      reference: e.reference as string,
      ...(e.expires !== undefined && { expires: String(e.expires) }),
    }));
  return {
    schema_version: 1,
    ...(isProfile(doc.profile) && { profile: doc.profile }),
    roles,
    ...(exceptions.length > 0 && { exceptions }),
  };
}

export function serializeRolesFile(file: RolesFile): string {
  const roles = Object.fromEntries(
    Object.entries(file.roles).filter(([, holders]) => (holders ?? []).length > 0),
  );
  return stringify({
    schema_version: 1,
    ...(file.profile && { profile: file.profile }),
    roles,
    ...(file.exceptions?.length && { exceptions: file.exceptions }),
  });
}

/** `.vivicta/asdp/profile-request.yaml`, the pending lowering request. */
export const serializeProfileRequest = (request: {
  from: Profile;
  to: Profile;
  reason: string;
  requested_by: string;
}) => stringify({ schema_version: 1, ...request });

/** The kit's gates each checkpoint covers. */
const CHECKPOINT_GATES: Partial<Record<Checkpoint, readonly string[]>> = {
  gate1: ['1'],
  requirements: ['2'],
  'setup-output': ['2', '3'],
  'apply-config': ['4'],
};

/** The kit's role keys (`role_mapping`) for the roles the portal tracks. */
export const KIT_ROLES: Readonly<Record<string, Role>> = {
  outcome_owner: 'product_owner',
  delivery_lead: 'project_manager',
  requirements_lead: 'business_analyst',
  architect: 'architect',
  qa: 'tester_qa',
  security_champion: 'security_champion',
  devsecops: 'devsecops',
  data_protection: 'dpo',
};

const portalRole = (key: unknown): Role | undefined =>
  typeof key === 'string' ? KIT_ROLES[key] ?? (isRole(key) ? key : undefined) : undefined;

/**
 * Routing declared by a template's `docs/sdlc/sdlc.json`: `gate_decision_owners`
 * and `gate_concurrences` keyed by the kit's gate numbers and role keys.
 * Roles outside the team (service owner, AI governance and so on) aren't
 * reviewers; checkpointConditions asks for their reference. A checkpoint the
 * file doesn't declare falls back to the portal default.
 */
export function routingFromSdlc(sdlc: unknown): Routing {
  if (!isObject(sdlc)) return defaultRouting;
  const owners = isObject(sdlc.gate_decision_owners) ? sdlc.gate_decision_owners : {};
  const concurrences = isObject(sdlc.gate_concurrences) ? sdlc.gate_concurrences : {};
  const roleList = (value: unknown, profile: Profile): Role[] => {
    const list = isObject(value) ? value[profile] ?? value.default : value;
    return (Array.isArray(list) ? list : [list])
      .map(portalRole)
      .filter((role): role is Role => role !== undefined);
  };
  return (checkpoint: Checkpoint, profile: Profile, context = {}) => {
    const gates = (CHECKPOINT_GATES[checkpoint] ?? []).filter(gate => gate in owners);
    if (gates.length === 0) return defaultRouting(checkpoint, profile, context);
    const approvals = gates.flatMap(gate => [
      ...roleList(owners[gate], profile).map((role): Approval => ({ role, kind: 'decides' })),
      ...roleList(concurrences[gate], profile).map((role): Approval => ({ role, kind: 'concurs' })),
    ]);
    return fitToTeam(approvals, checkpoint, profile, context);
  };
}

/** What routing needs from a recorded `.vivicta/asdp/gate1.json`. */
export function routingContextOfGate1(file: unknown): RoutingContext {
  if (!isObject(file)) return {};
  const answers = isObject(file.classification) ? file.classification.answers : undefined;
  return {
    personalData: isObject(answers) && hasPersonalData(answers as ClassificationAnswers),
    customerAcceptance:
      isObject(file.contract) && file.contract.customer_acceptance_authority === true,
  };
}

/** The profile a template records in `.vivicta/control.json`, if any. */
export const profileFromControl = (control: unknown): Profile | undefined =>
  isObject(control) && isProfile(control.profile) ? control.profile : undefined;
